import { useState, type FormEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalDatepicker } from './GlobalDatepicker';
import { GlobalInput } from './GlobalInput';
import { GlobalSwitch } from './GlobalSwitch';
import { GlobalTextarea } from './GlobalTextarea';
import type { RealEstateProjectTask } from '../types';

export type ProjectTaskFormValues = {
  title: string;
  dueDate?: string;
  assignee?: string;
  notes?: string;
  addToGlobalTasks?: boolean;
};

type Props = {
  task?: Pick<
    RealEstateProjectTask,
    'title' | 'dueDate' | 'assignee' | 'notes' | 'addToGlobalTasks'
  >;
  saving?: boolean;
  error?: string | null;
  submitLabel?: string;
  onSubmit: (values: ProjectTaskFormValues) => void;
  onCancel: () => void;
};

export function ProjectTaskForm({
  task,
  saving = false,
  error,
  submitLabel = 'Add task',
  onSubmit,
  onCancel,
}: Props) {
  const [title, setTitle] = useState(task?.title ?? '');
  const [dueDate, setDueDate] = useState(task?.dueDate ?? '');
  const [assignee, setAssignee] = useState(task?.assignee ?? '');
  const [notes, setNotes] = useState(task?.notes ?? '');
  const [addToGlobalTasks, setAddToGlobalTasks] = useState(task?.addToGlobalTasks ?? false);
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError('Task title is required.');
      return;
    }

    setValidationError(null);
    onSubmit({
      title: trimmedTitle,
      dueDate: dueDate.trim() || undefined,
      assignee: assignee.trim() || undefined,
      notes: notes.trim() || undefined,
      addToGlobalTasks,
    });
  }

  const displayError = validationError ?? error;

  return (
    <form className="sidebarFormStack" onSubmit={handleSubmit}>
      <GlobalInput
        label="Task title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Submit permit application"
        autoComplete="off"
        required
      />

      <GlobalDatepicker
        label="Due date (optional)"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
      />

      <GlobalInput
        label="Assignee (optional)"
        value={assignee}
        onChange={(e) => setAssignee(e.target.value)}
        placeholder="e.g. Architect, Me, Contractor"
        autoComplete="off"
      />

      <GlobalTextarea
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Additional details..."
        maxLength={1000}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <GlobalSwitch
          checked={addToGlobalTasks}
          onChange={() => setAddToGlobalTasks(!addToGlobalTasks)}
        />
        <span style={{ fontSize: '0.9375rem' }}>Add to global task list</span>
      </div>
      <p className="muted" style={{ margin: '-8px 0 0', fontSize: '0.8125rem' }}>
        When enabled, this task also appears in your main Tasks view.
      </p>

      {displayError ? <p className="sidebarFormError">{displayError}</p> : null}

      <div className="sidebarFormActions">
        <GlobalButton type="button" variant="secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </GlobalButton>
        <GlobalButton type="submit" variant="primary" isLoading={saving} disabled={saving}>
          {submitLabel}
        </GlobalButton>
      </div>
    </form>
  );
}
